import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/stock-card.css';

const StockCard = ({ stock }) => {
  const navigate = useNavigate();
  
  // Format price with currency
  const formatPrice = (price) => {
    if (!price) return '$--';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Format large numbers (volume)
  const formatVolume = (volume) => {
    if (!volume) return '--';
    if (volume >= 1000000) {
      return (volume / 1000000).toFixed(1) + 'M';
    } else if (volume >= 1000) {
      return (volume / 1000).toFixed(1) + 'K';
    }
    return volume.toLocaleString();
  };

  // Calculate price change percentage
  const getPriceChange = () => {
    if (!stock.close || !stock.day_open) return 0;
    const change = ((stock.close - stock.day_open) / stock.day_open) * 100;
    return change;
  };

  const priceChange = getPriceChange();
  
  // Get logo URL using the proxy endpoint
  const getLogoUrl = () => {
    return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/polygon/logos/${stock.ticker}/logo`;
  };

  const handleBuy = () => {
    // Call parent function to open modal if provided
    if (typeof window.openStockModal === 'function') {
      window.openStockModal(stock);
    } else {
      navigate(`/buy/${stock.ticker}`, { state: { asset: stock } });
    }
  };

  const handleViewDetails = () => {
    // Call parent function to open modal if provided  
    if (typeof window.openStockModal === 'function') {
      window.openStockModal(stock);
    } else {
      navigate(`/asset/${stock.ticker}`);
    }
  };

  return (
    <div className="stock-card">
      <div className="stock-card-header">
        <div className="stock-logo-container">
          <img 
            src={getLogoUrl()} 
            alt={`${stock.ticker} logo`}
            className="stock-logo"
            onError={(e) => {
              // Fallback to initial letter if logo fails
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="stock-logo-fallback" style={{display: 'none'}}>
            {stock.ticker.charAt(0)}
          </div>
        </div>
        
        <div className="stock-info">
          <h3 className="stock-ticker">{stock.ticker}</h3>
          <p className="stock-name">{stock.name}</p>
        </div>
        
        <div className={`stock-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
          <span className="change-percent">
            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
          </span>
          <span className="change-arrow">
            {priceChange >= 0 ? '↑' : '↓'}
          </span>
        </div>
      </div>

      <div className="stock-card-body">
        <div className="price-section">
          <div className="current-price">
            <span className="price-label">Current Price</span>
            <span className="price-value">{formatPrice(stock.close || stock.currentPrice)}</span>
          </div>
          
          <div className="bid-ask-section">
            <div className="bid-price">
              <span className="bid-label">Bid (Low)</span>
              <span className="bid-value">{formatPrice(stock.day_low)}</span>
            </div>
            <div className="ask-price">
              <span className="ask-label">Ask (High)</span>
              <span className="ask-value">{formatPrice(stock.day_high)}</span>
            </div>
          </div>
        </div>

        <div className="volume-section">
          <span className="volume-label">Volume</span>
          <span className="volume-value">{formatVolume(stock.volume)}</span>
        </div>
      </div>

      <div className="stock-card-footer">
        <button 
          className="details-button"
          onClick={handleViewDetails}
        >
          View Details
        </button>
        <button 
          className="buy-button"
          onClick={handleBuy}
        >
          Buy Now
        </button>
      </div>
    </div>
  );
};

export default StockCard;